import { useNavigate } from "react-router-dom";
import { WelcomeFrame } from "@/components/voice-onboarding/WelcomeFrame";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";
import type { VoiceMascotKey } from "@/types/voice-vision";
import { SuccessBadge, ShareLinkHero, MiniChannels } from "./LaunchBits";
import { ONBOARD_ROUTES, VOICE_ONBOARD_STEPS } from "./steps";

/**
 * Step 7 of 7 — Launch. The payoff: the genie was built, tuned and tested
 * entirely in onboarding, and now goes live without ever entering the app.
 *
 * The genie is REAL by the time the owner gets here — step 4 created it and
 * its activation pack. So the share link is the genie's actual public page and
 * "Go to my genie" opens the genie that exists, not a placeholder id.
 *
 * If step 4 could not create anything (network, permissions), the owner is not
 * shown a share link to a page that does not exist. They are sent back to the
 * step that failed.
 */
const LaunchPage = () => {
  const navigate = useNavigate();
  const { draft } = useOnboarding();

  const mascot: VoiceMascotKey = draft.genieType?.mascot ?? "receptionist";
  const genieName = draft.genieName ?? draft.genieType?.title ?? "Your genie";

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://helpgenie.ai";
  // Only a real url_name produces a link. No slugified guess at a page that
  // may not exist — a dead share link is worse than no share link.
  const shareUrl = draft.urlName ? `${origin}/live-agent/${draft.urlName}` : null;

  const handleContinue = () => {
    if (draft.genieId) {
      navigate(`/genie-hub/my-genies/${draft.genieId}`);
      return;
    }
    // Nothing was created — send them back to the step that does the work.
    navigate(ONBOARD_ROUTES[3]);
  };

  return (
    <WelcomeFrame
      step={7}
      steps={VOICE_ONBOARD_STEPS}
      eyebrow={draft.genieId ? "YOU'RE LIVE" : "ALMOST THERE"}
      title={
        draft.genieId
          ? `${genieName} is ready for customers`
          : "Your genie has not been built yet"
      }
      subtitle={
        draft.genieId
          ? "You built, tuned and tested it, all before opening the app. Share it now, or head in to fine-tune anything."
          : "Go back a step and we will build it. Nothing you have entered is lost."
      }
      showMascot={false}
      showSkip={false}
      continueLabel={draft.genieId ? "Go to my genie" : "Back to building"}
      footerNote={
        draft.genieId
          ? "You can set all of this up later in the app too."
          : "Nothing was saved, so there is nothing to undo."
      }
      onBack={() => navigate(ONBOARD_ROUTES[5])}
      onContinue={handleContinue}
    >
      <div className="w-full flex flex-col items-center gap-7">
        <SuccessBadge mascot={mascot} />
        {shareUrl && <ShareLinkHero url={shareUrl} />}
        {draft.genieId && !shareUrl && (
          <p className="text-center text-[13px] text-[#64748B] max-w-md">
            Your genie is live. Its shareable page is still being set up, and it will be waiting for
            you on the genie's page in a moment.
          </p>
        )}
        {draft.knowledgeSummary?.attempted && !draft.knowledgeSummary.ok && (
          <p className="text-center text-[13px] text-[#64748B] max-w-md">
            One thing left: we could not read your website, so your genie does not know your hours,
            services or pricing yet. Add them from the knowledge page whenever you are ready.
          </p>
        )}
        <MiniChannels />
      </div>
    </WelcomeFrame>
  );
};

export default LaunchPage;
